import {
	SlipSheet_t,
	SlipSheetConfig_t,
	getSlipSheet,
	SlipSheetOption,
	Pallet_t,
	LayoutTransform,
	Stack as StackObject,
} from 'blackbox';

import { AutoStackParams } from './stackTypes';

/**
 * Returns the height of the slip sheet at the given layer.
 */
export function getSlipSheetHeight(
	layerIndex: number,
	totalLayers: number,
	availableSlipSheets: SlipSheet_t[],
	slipSheetConfig: SlipSheetConfig_t | undefined
): number {
	const sheet = getSlipSheet(
		layerIndex,
		totalLayers,
		availableSlipSheets,
		slipSheetConfig
	);
	return sheet ? sheet.dimensions.height : 0;
}

/**
 * Creates a default slip sheet config with no sheets enabled.
 */
export function createDefaultSlipSheetConfig(): SlipSheetConfig_t {
	return {
		config: SlipSheetOption.None,
		exclude: {},
		ids: {},
	};
}

/**
 * A human-readable mapping of slip sheet option labels.
 */
export const slipSheetOptionLabels: [string, SlipSheetOption][] = [
	['None', SlipSheetOption.None],
	[
		'Yes, between every layer except top and bottom',
		SlipSheetOption.YesExceptTopBottom,
	],
	['Yes, between every layer except top', SlipSheetOption.YesExceptTop],
	['Yes, between every layer except bottom', SlipSheetOption.YesExceptBottom],
	['Yes, between every layer including top and bottom', SlipSheetOption.Yes],
];

/**
 * Attempts to detect whether a stack is an "auto stack" and returns matching params if so.
 */
export function inferAutoStackParamsFromStack(
	pallets: Pallet_t[],
	palletIndex: number,
	stackIndex: number
): AutoStackParams | null {
	const stack = pallets[palletIndex].Stack[stackIndex];
	const autoStackParams = createDefaultAutoStackParams(
		palletIndex,
		stack.layers.length,
		pallets
	);

	autoStackParams.slipSheet = stack.slipSheet || createDefaultSlipSheetConfig();

	if (stack.layers.length <= 1) {
		if (stack.layers.length === 0) return autoStackParams;

		autoStackParams.layoutIndex = stack.layers[0].index;
		autoStackParams.oddTransform = stack.layers[0].transform;
		return autoStackParams;
	}

	for (let layerIndex = 0; layerIndex < stack.layers.length; layerIndex++) {
		const layer = stack.layers[layerIndex];

		if (layerIndex === 0) {
			autoStackParams.layoutIndex = layer.index;
			autoStackParams.oddTransform = layer.transform;
			continue;
		}

		if (layer.index !== autoStackParams.layoutIndex) return null;

		if (layerIndex === 1) {
			autoStackParams.evenTransform = layer.transform;
			continue;
		}

		const isOdd = (layerIndex + 1) % 2 === 1;
		const expectedTransform = isOdd
			? autoStackParams.oddTransform
			: autoStackParams.evenTransform;

		if (layer.transform !== expectedTransform) return null;
	}

	return autoStackParams;
}

/**
 * Provides default params to initialize an auto stack configuration.
 */
export function createDefaultAutoStackParams(
	palletIndex: number,
	layerCount: number,
	pallets: Pallet_t[]
): AutoStackParams {
	return {
		palletIndex,
		layoutIndex: 0,
		layerCount:
			layerCount === 0
				? pallets[palletIndex].Layouts.length > 0
					? 4
					: 0
				: layerCount,
		slipSheet: createDefaultSlipSheetConfig(),
		evenTransform: LayoutTransform.TransformType.Identity,
		oddTransform: LayoutTransform.TransformType.Identity,
	};
}
